import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Check } from "lucide-react";
import { useRole } from "@/contexts/RoleContext";
import { makeApi } from "@/lib/api";
import { drugTestingDone } from "@/lib/drugTesting";

const STEPS = [
  { to: "/dashboard", label: "Your Carrier" },
  { to: "/compliance", label: "Documents" },
  { to: "/compliance#drug-testing", label: "Drug Testing", status: true },
  { to: "/pricing", label: "Pick a Plan" },
];

function useDrugDone(enabled) {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const refresh = () => api.get("/profile").then(({ data }) => setDone(drugTestingDone(data))).catch(() => {});
    refresh();
    window.addEventListener("drug-testing-changed", refresh);
    return () => window.removeEventListener("drug-testing-changed", refresh);
  }, [api, enabled]);
  return done;
}

export default function FlowSteps({ current }) {
  const { actingAsAdmin } = useRole();
  const drugDone = useDrugDone(!actingAsAdmin);
  if (actingAsAdmin) return null;
  const pageIndex = [0, 1, 3][current];
  return (
    <nav className="mb-8" aria-label="Progress" data-testid="flow-steps">
      <div className="font-mono-label text-[10px] text-muted-foreground mb-2" data-testid="flow-steps-label">
        STEP {pageIndex + 1} OF {STEPS.length}
      </div>
      <ol className="grid grid-cols-4 gap-1">
        {STEPS.map((s, i) => {
          const state = s.status ? (drugDone ? "done" : "todo") : i < pageIndex ? "done" : i === pageIndex ? "current" : "todo";
          return (
            <li key={s.to}>
              <Link to={s.to} data-testid={`flow-step-${i + 1}`} aria-current={state === "current" ? "step" : undefined}
                onClick={() => { if (s.status) setTimeout(() => document.getElementById("drug-testing")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                className="group block">
                <div className={`h-1.5 mb-2 transition-colors ${state === "todo" ? "bg-[#262626] group-hover:bg-[#3a3a3a]" : "bg-[#FFD600]"}`} />
                <div className={`flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold ${state === "current" ? "text-white" : state === "done" ? "text-[#FFD600]" : "text-muted-foreground group-hover:text-white"} transition-colors`}>
                  <span className={`w-5 h-5 shrink-0 inline-flex items-center justify-center font-mono-label text-[10px] ${state === "todo" ? "border border-[#262626]" : "bg-[#FFD600] text-[#0A0A0A]"}`}>
                    {state === "done" ? <Check size={12} strokeWidth={3} /> : i + 1}
                  </span>
                  <span className="truncate">{s.label}</span>
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
